import { sanitizeCmsHtml } from '../sanitize-html';

const BLOCK_TAG = '(?:blockquote|h[1-6]|hr|li|ol|p|pre|ul)';
const BLOCK_OPEN_PATTERN = new RegExp(`<${BLOCK_TAG}\\b[^>]*>`, 'gi');
const BLOCK_CLOSE_PATTERN = new RegExp(`</${BLOCK_TAG}\\s*>`, 'gi');
const VOID_BLOCK_PATTERN = /<hr\b[^>]*\/?>/gi;
const IMPORTED_BLOCK_CLOSE_PATTERN = /<\/(?:article|aside|div|footer|header|main|nav|section)\s*>/gi;
// Block boundaries collapse separately from authored <br> nodes so Enter at the end of a
// heading and intentional blank lines survive resolveData and production rendering.
const LEADING_OR_TRAILING_BOUNDARY_PATTERN = /^\s*\n\s*|\s*\n\s*$/g;
const REPEATED_BOUNDARY_PATTERN = /\n(?:\s*\n)+/g;
const ALIGNMENT_PATTERN = /<(?:h[1-6]|p)\b[^>]*\bstyle="text-align: (left|center|right|justify)"/i;

type HeadingLevel = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

function headingInlineHtml(html: unknown): { alignment?: string; inlineHtml: string } {
  // Preserve common imported block boundaries locally; the shared sanitizer remains general.
  // Literal newlines in raw HTML are whitespace; authored line breaks are explicit <br> nodes.
  const source = typeof html === 'string' ? html.replace(/\r?\n/g, ' ') : '';
  const withImportedBoundaries = source.replace(IMPORTED_BLOCK_CLOSE_PATTERN, '\n');
  const sanitized = sanitizeCmsHtml(withImportedBoundaries);
  return {
    alignment: ALIGNMENT_PATTERN.exec(sanitized)?.[1],
    inlineHtml: sanitized
      .replace(VOID_BLOCK_PATTERN, '\n')
      .replace(BLOCK_CLOSE_PATTERN, '\n')
      .replace(BLOCK_OPEN_PATTERN, '')
      .replace(REPEATED_BOUNDARY_PATTERN, '\n')
      .replace(LEADING_OR_TRAILING_BOUNDARY_PATTERN, '')
      .replace(/\n/g, '<br>'),
  };
}

export function semanticHeadingHtml(html: string, tag: HeadingLevel): string {
  const { alignment, inlineHtml } = headingInlineHtml(html);
  const meaningfulText = inlineHtml
    .replace(/<[^>]*>/g, '')
    .replace(/(?:&nbsp;|&#160;|&#xa0;)/gi, '')
    .trim();
  if (meaningfulText === '') {
    return '';
  }
  const style = alignment ? ` style="text-align: ${alignment}"` : '';
  return `<${tag}${style}>${inlineHtml}</${tag}>`;
}

export function normalizeHeadingEditorHtml(html: unknown): string {
  const { alignment, inlineHtml } = headingInlineHtml(html);
  const style = alignment ? ` style="text-align: ${alignment}"` : '';
  return `<p${style}>${inlineHtml}</p>`;
}
